import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { AirflowTaskInstance } from '../../types';

/*
 * Manages the Clear DAG Run dialog's toggle options and live dry-run preview
 */
export function useClearDagRunDialog(dagId: string, runId: string, onCleared: () => void) {
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const [onlyFailed, setOnlyFailed] = useState(false);
    const [runOnLatestVersion, setRunOnLatestVersion] = useState(false);
    const [affectedTasks, setAffectedTasks] = useState<AirflowTaskInstance[]>([]);
    const [isLoadingPreview, setIsLoadingPreview] = useState(true);
    const [isClearing, setIsClearing] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setIsLoadingPreview(true);
        AirflowDashboardHandler.clearDagRun(Env, dagId, runId, { dryRun: true, onlyFailed, runOnLatestVersion })
            .then((response) => {
                if (!cancelled) setAffectedTasks(response.task_instances);
            })
            .catch(() => {
                if (!cancelled) errorAlert('Failed to preview affected tasks.');
            })
            .finally(() => {
                if (!cancelled) setIsLoadingPreview(false);
            });
        return () => {
            cancelled = true;
        };
    }, [Env, dagId, runId, onlyFailed, runOnLatestVersion]);

    /*
     * Executes the clear for real, then notifies the caller to refresh
     */
    const handleConfirm = useCallback(async () => {
        setIsClearing(true);
        try {
            await AirflowDashboardHandler.clearDagRun(Env, dagId, runId, { dryRun: false, onlyFailed, runOnLatestVersion });
            successAlert('DAG run cleared.');
            onCleared();
        } catch {
            errorAlert('Failed to clear DAG run.');
        } finally {
            setIsClearing(false);
        }
    }, [Env, dagId, runId, onlyFailed, runOnLatestVersion, onCleared]);

    return { onlyFailed, setOnlyFailed, runOnLatestVersion, setRunOnLatestVersion, affectedTasks, isLoadingPreview, isClearing, handleConfirm };
}
